import { FC, useCallback, useEffect, useState } from "react";
import ProductCard from "components/cards/ProductCard";

import {
  getAllProducts,
  getProducts,
} from "services/companyManagementRequests";
import { useCommonContext } from "hooks‬/CommonContext";
import SubscriptionDetailCard from "./SubscriptionDetailCard";
import { useAuth } from "hooks‬/Auth";
import PageTitle from "components/base/PageTitle";
import { useFeatureAccess } from "hooks‬/custome-hooks/useFeatureAccess";
import { featureObject } from "helpers/privileges/utils";
import PageLoader from "components/base/PageLoader";
import apiInterceptor from "services/apiInterceptor";
import { subscriptionApiEndpoint } from "constant/apiConstants";
import DashboardGreeting from "./DashboardGreeting";
import "./Dashboard.scss";

const Dashboard: FC = () => {
  const { showErrorToast, showSuccessToast } = useCommonContext();
  const {
    companyProductSubscriptionDetails,
    userDetails,
    currentCompany,
    fetchSubcriptionDetail,
  } = useAuth();
  const [purchasedProduct, setPurchasedProduct] = useState<any[]>([]);
  const [otherProduct, setOtherProduct] = useState<any[]>([]);
  const [allProduct, setAllProduct] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handleProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await getProducts();
      if (response.data.status === "ERROR") {
        showErrorToast(response.data.statusDesc);
      } else {
        setPurchasedProduct(response.data.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  const handleAllProducts = useCallback(async () => {
    try {
      const response: any = await getAllProducts();
      if (response.data.status === "ERROR") {
        showErrorToast(response.data.statusDesc);
      } else {
        setAllProduct(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    handleAllProducts();
    handleProducts();
  }, [handleAllProducts, handleProducts]);

  useEffect(() => {
    if (purchasedProduct.length > 0) {
      const purchasedGuids = purchasedProduct.map(
        (item) => item.productTypeDTO.guid
      );

      const notPurchasedApps = allProduct.filter(
        (app) => !purchasedGuids.includes(app.guid)
      );
      setOtherProduct(notPurchasedApps);
    } else {
      setOtherProduct(allProduct);
    }
  }, [allProduct, purchasedProduct]);

  const subscription = useFeatureAccess(featureObject.SUBSCRIPTION);
  if (isLoading) {
    return <PageLoader />;
  }

  const handleAddBeta = async () => {
    apiInterceptor
      .post(
        `${subscriptionApiEndpoint.ENDPOINT_CREATE_COMPANY}/${currentCompany?.companyDetailsGuid}/beta-subscriptions`,
        {
          subscriptionPricingPlanGuid: "6cba5acc58aa44b2a5fae3fd34729e2f",
          subscriptionType: "BETA",
          userGuid: userDetails?.guid,
        }
      )
      .then(() => {
        handleAllProducts();
        handleProducts();
        fetchSubcriptionDetail();
        showSuccessToast("Added to beta successfully");
      })
      .catch((error) => {
        console.error(error);
        showErrorToast("Failed to add to beta");
      });
  };

  return (
    <div className="ni-dash-root">
      <PageTitle title="Dashboard" />

      <DashboardGreeting />

      {subscription.canRead && (
        <SubscriptionDetailCard
          companySubscriptionDetailsList={companyProductSubscriptionDetails}
        />
      )}

      {purchasedProduct.length > 0 && (
        <>
          <div className="ni-dash-section-header">
            <div>
              <h2 className="ni-dash-section-title">Your apps</h2>
              <div className="ni-dash-section-count">
                {purchasedProduct.length}{" "}
                {purchasedProduct.length === 1 ? "app" : "apps"} active
              </div>
            </div>
          </div>
          <div className="ni-dash-apps-grid">
            {purchasedProduct.map((item: any) => {
              const matchedSubscription = companyProductSubscriptionDetails?.find(
                (sub: any) =>
                  sub?.subscriptionPricingPlan?.subscriptionPlanProduct
                    ?.productType?.guid === item.productTypeDTO.guid
              );
              return (
                <ProductCard
                  key={item.productTypeDTO.name}
                  productGuid={item.productTypeDTO.guid}
                  productTitle={item.productTypeDTO.name}
                  productDescription={item?.productTypeDTO?.shortDescription}
                  isExpired={matchedSubscription?.isExpired ?? false}
                  canSubscribe={subscription.canRead}
                />
              );
            })}
          </div>
        </>
      )}

      {otherProduct.length > 0 && (
        <>
          <div className="ni-dash-section-header">
            <div>
              <h2 className="ni-dash-section-title">
                {purchasedProduct.length > 0 ? "Other apps" : "Available apps"}
              </h2>
              <div className="ni-dash-section-count">
                {otherProduct.length}{" "}
                {otherProduct.length === 1 ? "app" : "apps"} available
              </div>
            </div>
          </div>
          <div className="ni-dash-apps-grid">
            {otherProduct.map((item: any) => (
              <ProductCard
                key={item.guid}
                productTitle={item.name}
                isBought={false}
                productGuid={item.guid}
                productDescription={item?.shortDescription}
                handleAddBeta={handleAddBeta}
                canSubscribe={subscription.canRead}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export { Dashboard };
